import Proptypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { CheckIcon, ChevronDown, PlusIcon } from 'lucide-react';
import { Button, DefaultTooltip, DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, Input, Separator } from 'twyne-new.ui';

import { useBubbleTiptap } from '../bubble/Bubble';

import FormatterIcon from './FormatterIcon';

export default function NewDropDown({ value, creatable = false, options: defaultOptions, onClickOption, tooltipText, icon: Icon, name: _name, editor: _editor, darkMode = false, ..._props }) {
  const { setBubblePopover } = useBubbleTiptap()

  const inputRef = useRef(null)

  const [open, setOpen] = useState(false)
  const [customValue, setCustomValue] = useState()
  const [options, setOptions] = useState()

  useEffect(() => {
    if (options === undefined) {
      setOptions(defaultOptions)
    }
  }, [options, defaultOptions, value])

  useEffect(() => {
    if (value && options?.length && !options.find(option => option === value || option?.value === value)) {
      setOptions(prev => [value, ...prev])
    }
  }, [value, options])

  useEffect(() => {
    setBubblePopover && setBubblePopover(open)
  }, [open, setBubblePopover])

  const handleClickOption = (event, option) => {
    event.preventDefault()
    event.stopPropagation()
    onClickOption(option?.value || option)
  }

  const handleCreateOption = () => {
    if (!customValue) return

    setOptions(prev => [`${customValue}px`, ...prev])
    setCustomValue('')
  }

  const handleChangeInput = e => {
    e.stopPropagation()
    inputRef.current.focus()
    if (e.key === 'Enter') {
      handleCreateOption()
    }
  }

  if (!options) {
    return null
  }

  return <DropdownMenu open={open} onOpenChange={setOpen} darkMode={darkMode}>
    <DropdownMenuTrigger className='relative'>
      <FormatterIcon
        icon={Icon}
        tooltipText={tooltipText}
      />
      <ChevronDown className='absolute right-0 bottom-0' size='10' />
    </DropdownMenuTrigger>
    <DropdownMenuContent className='py-1 pr-0 w-[250px]' >
      {creatable && <>
        <div className='flex gap-1'>
          <Input
            ref={inputRef}
            type='number'
            value={customValue}
            onChange={e => setCustomValue(e.target.value)}
            placeholder='Type to create a custom value'
            className='w-full'
            onKeyDown={handleChangeInput}
            onMouseDown={e => e.stopPropagation()}
          />
          <DefaultTooltip content='Create custom value' triggerProps={{ asChild: true }}>
            <Button variant='light' size='sm' onClick={handleCreateOption}>
              <PlusIcon size='14' />
            </Button>
          </DefaultTooltip>
        </div>
        <Separator className='my-2' />
      </>}
      <div className='max-h-56 overflow-y-auto pr-2'>
        {options.map((option, key) => <DropdownItem key={key} option={option} onClick={event => handleClickOption(event, option)} active={value && (option === value || option?.value === value)} />)}
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
}

const DropdownItem = ({ option, active, ...props }) => <div {...props} className='flex gap-2 items-center rounded-xs px-2 py-1.5 twy-text-base hover:bg-gray-200 cursor-pointer'>
  {option.icon ? <option.icon size={15} /> : option?.component || option?.label || option}
  {active && <CheckIcon size={14} />}
</div>

NewDropDown.propTypes = {
  options: Proptypes.array,
  icon: Proptypes.any,
  handleClickOption: Proptypes.func,
  tooltipText: Proptypes.string,
}
